import plur from 'plur';
import type { ObjectType } from '@/services/db/object-type/entity';
import { ImportType, type SqlImport } from '@/services/db/sql-import/entity';
import type { SqlImportRow } from '@/services/db/sql-import-row/entity';
import { SqlDatabaseType, type SqlImportDatabase } from '@/services/db/sql-import-database/entity';
import { sqlImportRowIsPending } from '@/services/db/sql-import-row/entity';
import { isEmptyString } from '@/utils/string';

/** Newest run first. */
export function sortRunsNewestFirst(rows: readonly SqlImportRow[]): SqlImportRow[] {
  return [...rows].sort((a, b) => b.created_at - a.created_at);
}

/** Whether any run is still moving, which decides how often the history is re-read. */
export function hasPendingRun(rows: readonly SqlImportRow[]): boolean {
  return rows.some((row) => sqlImportRowIsPending(row));
}

/** How often the history is re-read: twice a minute while a run is moving, once a minute otherwise. */
export function rowsRefetchIntervalMs(rows: readonly SqlImportRow[] | undefined): number {
  return rows != null && hasPendingRun(rows) ? 30_000 : 60_000;
}

export type RunCount = { count: number; label: string };

/**
 * What a finished run did, as the counts Ember listed: new, existing and failed people (or
 * relationships synced), each only when non-zero.
 */
export function runCounts(
  row: Pick<SqlImportRow, 'added_rows' | 'updated_rows' | 'failed_rows' | 'import_type'>,
): RunCount[] {
  const isRelationship = row.import_type === 'relationship';
  const total = row.added_rows + row.updated_rows + row.failed_rows;
  const label = (what: string) => (isRelationship ? `${plur('relationship', total)} synced` : what);
  const counts: RunCount[] = [];
  if (row.added_rows > 0) {
    counts.push({ count: row.added_rows, label: label('new') });
  }
  if (row.updated_rows > 0) {
    counts.push({ count: row.updated_rows, label: label('existing') });
  }
  if (row.failed_rows > 0) {
    counts.push({ count: row.failed_rows, label: label('failed') });
  }
  return counts;
}

/** What the runs sync, in lower case and in both numbers: "person"/"people", or the object type's names. */
export type TargetNouns = { singular: string; plural: string };

/**
 * What an import's runs sync: people as the workspace names them, or the object type's singular and
 * plural names, or plain objects when the import's object type is no longer listed.
 */
export function runTargetNouns(
  sqlImport: Pick<SqlImport, 'import_type' | 'object_type_id'>,
  objectTypes: readonly Pick<ObjectType, 'id' | 'name' | 'singular_name'>[],
  people: TargetNouns,
): TargetNouns {
  if (sqlImport.import_type !== ImportType.object) {
    return people;
  }
  const objectType = objectTypes.find((candidate) => candidate.id === sqlImport.object_type_id);
  if (objectType == null) {
    return { singular: 'object', plural: 'objects' };
  }
  return { singular: objectType.singular_name.toLowerCase(), plural: objectType.name.toLowerCase() };
}

/** What was synced, agreeing in number with how many rows the run handled. */
export function targetNoun(nouns: TargetNouns, count: number): string {
  return count === 1 ? nouns.singular : nouns.plural;
}

export type RunSummaryPart = RunCount & {
  /** The words before this part: nothing, ", ", " and " between two parts, ", and " before the last of three. */
  separator: string;
};

function separatorBefore(index: number, total: number): string {
  if (index === 0) {
    return '';
  }
  if (index < total - 1) {
    return ', ';
  }
  return total > 2 ? ', and ' : ' and ';
}

/** The counts with the words that join them, as Ember listed them: "a and b", "a, b, and c". */
export function runSummaryParts(
  row: Pick<SqlImportRow, 'added_rows' | 'updated_rows' | 'failed_rows' | 'import_type'>,
): RunSummaryPart[] {
  const counts = runCounts(row);
  return counts.map((count, index) => ({ ...count, separator: separatorBefore(index, counts.length) }));
}

/**
 * Where a run read from, in the shapes a run row can name it by.
 *
 * A BigQuery run has no host to name, so it is named by project and dataset location, the way the
 * configuration panel names the same database. Every other engine records `host:name` and is named
 * by the name in it — including Snowflake, whose account name takes the host's place.
 */
export type RunDatabaseSource =
  /** BigQuery: the project the run read from, and the location of its dataset. */
  | { kind: 'project'; projectId: string; location: string }
  /** Every other engine: the database name the run recorded. */
  | { kind: 'name'; name: string }
  /** The run recorded nothing to name it by. */
  | { kind: 'unknown' };

/** The database a run read from, as the run row names it. */
export function runDatabaseSource(
  row: Pick<SqlImportRow, 'db_host_and_name' | 'location'>,
  database: Pick<SqlImportDatabase, 'type' | 'project_id'> | undefined,
): RunDatabaseSource {
  if (database?.type === SqlDatabaseType.bigQuery) {
    return { kind: 'project', projectId: database.project_id, location: row.location };
  }
  const [, name] = row.db_host_and_name.split(':');
  return isEmptyString(name) ? { kind: 'unknown' } : { kind: 'name', name };
}

/** The wire's `import_type` as the union the summaries take; anything unknown reads as a people import. */
export function importTypeOf(value: string): ImportType {
  return Object.values(ImportType).find((candidate) => candidate === value) ?? ImportType.workspace;
}
